/**
 * OnboardingModalLayout - Web layout for onboarding wizard
 *
 * Matches Settings modal design exactly but without sidebar.
 * Full-page layout that looks like a modal. Renders per-step
 * headline, description, help tooltip, and context callout
 * via step metadata props.
 */

import type { ReactNode } from "react";
import { cn } from "../utils/cn";
import { ProgressDots } from "./ProgressDots";
import { ProgressBar } from "./ProgressBar";
import { HelpTooltip } from "./HelpTooltip";
import { ContextCallout } from "./ContextCallout";

interface OnboardingModalLayoutProps {
  /** Current screen index (0-based) */
  currentIndex: number;
  /** Total number of screens */
  totalScreens: number;
  /** Category name to display above progress dots */
  categoryName: string;
  /** Whether there is a previous screen */
  hasPrevious: boolean;
  /** Whether there is a next screen */
  hasNext: boolean;
  /** Whether "Done" button should be shown */
  showDone: boolean;
  /** Handler for previous button */
  onPrevious: () => void;
  /** Handler for next button */
  onNext: () => void;
  /** Handler for done button */
  onDone: () => void;
  /** Whether the user can complete onboarding (profile fields set) */
  canComplete?: boolean;
  /** List of missing required fields */
  missingFields?: string[];
  /** Whether the next button should be disabled (e.g., while loading statistics) */
  nextDisabled?: boolean;
  /** Step headline (e.g., "Let's get to know you") — falls back to categoryName */
  headline?: string;
  /** Step description paragraph */
  description?: string;
  /** Tooltip content for the "?" help button */
  tooltipContent?: string;
  /** Aria label for the tooltip trigger */
  tooltipAriaLabel?: string;
  /** Context callout text (💡 prefix) */
  contextCalloutText?: string;
  /** Handler for "Use smart defaults" skip link */
  onSkip?: () => void;
  /** Form content to display */
  children: ReactNode;
}

/**
 * OnboardingModalLayout - Layout matching Settings modal design
 *
 * Features:
 * - 1000px width, 80vh height (responsive, taller than Settings modal)
 * - Step headline with description and help tooltip
 * - Context callout for each step
 * - Desktop: "Step X of 7" + ProgressDots
 * - Mobile: ProgressBar
 * - Previous/Next buttons (30% smaller, same gray color)
 * - Green Done button with checkmark
 * - "Use smart defaults" skip link in footer
 * - No sidebar (full content area)
 * - Validation for required profile fields before completion
 */
export function OnboardingModalLayout({
  currentIndex,
  totalScreens,
  categoryName,
  hasPrevious,
  hasNext,
  showDone,
  onPrevious,
  onNext,
  onDone,
  canComplete = true,
  missingFields = [],
  nextDisabled = false,
  headline,
  description,
  tooltipContent,
  tooltipAriaLabel,
  contextCalloutText,
  onSkip,
  children,
}: OnboardingModalLayoutProps) {
  const isDoneDisabled = showDone && !canComplete;
  const displayTitle = headline ?? categoryName;

  return (
    <div className="onboarding-modal-centered" id="onboarding-wizard">
      {/* Container matching Settings modal size - increased height for better form visibility */}
      <div className="onboarding-container">
        {/* Header with step info and progress */}
        <div className="onboarding-header">
          {/* Mobile progress bar (hidden on desktop via CSS) */}
          <div className="onboarding-mobile-progress">
            <ProgressBar current={currentIndex} total={totalScreens} />
          </div>

          {/* Step headline */}
          <h2 className="onboarding-step-headline">{displayTitle}</h2>

          {/* Description row with help tooltip */}
          {(description || tooltipContent) && (
            <div className="onboarding-step-description-row">
              {description && (
                <p className="onboarding-step-description">{description}</p>
              )}
              {tooltipContent && tooltipAriaLabel && (
                <HelpTooltip
                  content={tooltipContent}
                  ariaLabel={tooltipAriaLabel}
                />
              )}
            </div>
          )}

          {/* Context callout */}
          {contextCalloutText && (
            <ContextCallout text={contextCalloutText} category={categoryName} />
          )}

          {/* Desktop: ProgressDots + step count (hidden on mobile via CSS) */}
          <div className="onboarding-desktop-progress">
            <div className="footer-center-aligned">
              <ProgressDots current={currentIndex} total={totalScreens} />
              <span className="onboarding-step-count">
                Step {currentIndex + 1} of {totalScreens}
              </span>
            </div>
          </div>
        </div>

        {/* Scrollable content area - matches Settings modal content dimensions */}
        <div className="onboarding-content">{children}</div>

        {/* Footer with navigation buttons */}
        <div className="onboarding-footer">
          {/* Previous button - blue like other buttons */}
          {hasPrevious ? (
            <button onClick={onPrevious} className="btn-prev">
              Previous
            </button>
          ) : (
            <div /> // Spacer
          )}

          {/* Right side: Done/Next button, error message, skip link */}
          <div className="footer-actions-column">
            {/* Error message for missing fields */}
            {isDoneDisabled && missingFields.length > 0 && (
              <p className="footer-error-message">
                Please set {missingFields.join(" and ")} in the Profile section
                to continue
              </p>
            )}

            {/* Next or Done button */}
            {showDone ? (
              <button
                onClick={onDone}
                disabled={isDoneDisabled}
                className={cn(
                  "btn-done",
                  isDoneDisabled && "btn-done-disabled"
                )}
              >
                <span>Done</span>
              </button>
            ) : hasNext ? (
              <button
                onClick={onNext}
                disabled={nextDisabled}
                className={cn("btn-next", nextDisabled && "btn-done-disabled")}
              >
                Next
              </button>
            ) : null}

            {/* Skip link */}
            {onSkip && (
              <>
                <img
                  className="onboarding-smart-defaults-image"
                  src="/images/onboarding/smart-defaults.png"
                  alt=""
                  aria-hidden="true"
                />
                <button
                  type="button"
                  onClick={onSkip}
                  className="onboarding-skip-defaults"
                >
                  Use smart defaults
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
